import { useEffect, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { RuleGroup } from "@trackmark/core";
import { isRunnable } from "@trackmark/core";
import { endpoints } from "../api.js";
import { useResource } from "../useResource.js";
import { Lists, type ListRow, type PreviewSong } from "./Lists.js";
import { Failed, Loading } from "./Loading.js";

const EMPTY: RuleGroup = { kind: "group", join: "and", children: [] };

/**
 * Lists, wired.
 *
 * The preview is debounced and the previous count HOLDS while a request is in flight. The header
 * promises the count updates as you type; flickering to zero between keystrokes would technically
 * honour that and would read as the rules being broken.
 */
export function ListsScreen() {
  const lists = useResource(() => endpoints.lists(), []);
  // `?edit=<id>` — "Edit rules" on a list's own screen opens it here.
  const { edit } = useSearch({ from: "/lists" });
  const navigate = useNavigate();
  const [editing, setEditing] = useState<{ id: number | null; name: string; rules: RuleGroup } | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [hints, setHints] = useState<{ excludes: number }[]>([]);
  const [preview, setPreview] = useState<PreviewSong[]>([]);
  const [saving, setSaving] = useState(false);

  const rules = editing?.rules ?? null;
  useEffect(() => {
    if (rules === null) return;
    // An unfinished rule is the normal state of a live-edited builder: hold the last real answer
    // rather than asking the server to count a tree that is not a question yet.
    if (!isRunnable(rules)) return;

    let live = true;
    const timer = setTimeout(() => {
      void endpoints.listPreview(rules).then((result) => {
        if (!live) return;
        setTotal(result.total);
        setHints(result.hints);
        setPreview(result.songs);
      });
    }, 140);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [rules]);

  /*
   * Opens the list named in `?edit=`. Once, on arrival: re-opening it whenever `lists` refetched
   * would fight the user closing the builder.
   */
  const saved = lists.data?.lists.find((l) => l.id === edit);
  useEffect(() => {
    if (saved === undefined) return;
    setEditing({ id: saved.id, name: saved.name, rules: saved.rules ?? EMPTY });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved?.id]);

  /** The preview belongs to whatever is open; carrying it across would show the last list's count. */
  const reset = () => {
    setTotal(null);
    setHints([]);
    setPreview([]);
  };

  if (lists.error) return <Failed error={lists.error} retry={lists.reload} />;
  if (lists.loading) return <Loading what="lists" />;

  return (
    <Lists
      lists={lists.data?.lists ?? []}
      onOpen={(list: ListRow) => {
        reset();
        setEditing({ id: list.id, name: list.name, rules: list.rules ?? EMPTY });
      }}
      onNew={() => {
        reset();
        setEditing({ id: null, name: "", rules: EMPTY });
      }}
      onClose={() => {
        reset();
        setEditing(null);
        // Drops `?edit=`, so closing the builder does not leave a URL that reopens it.
        if (edit !== undefined) void navigate({ to: "/lists", search: {} });
      }}
      onDelete={(id) => void endpoints.deleteList(id).then(() => lists.reload())}
      editing={editing}
      onName={(name) => setEditing((e) => (e === null ? e : { ...e, name }))}
      onRules={(next) => setEditing((e) => (e === null ? e : { ...e, rules: next }))}
      onSave={() => {
        if (editing === null) return;
        setSaving(true);
        const write =
          editing.id === null
            ? endpoints.createList(editing.name, editing.rules)
            : endpoints.updateList(editing.id, editing.name, editing.rules);
        void write
          .then(() => {
            lists.reload();
            setEditing(null);
          })
          .finally(() => setSaving(false));
      }}
      saving={saving}
      total={total}
      hints={hints}
      preview={preview}
    />
  );
}
